import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Divider } from '@mui/material';

import { IDocument } from 'common/document/interfaces/document';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { LogEvents } from 'components/Logs/events';
import { DocumentImageViewer } from '../DocumentImageViewer';
import { DocumentReviewHeader } from '../DocumentViewHeader';
import {
  DocumentStatusEnum,
  readOnlyViewableStatuses,
} from 'common/document/enums/document-status.enum';
import { DocumentFiles } from 'common/document/interfaces/document-files';
import { originalSuffix } from 'common/document/constants/original-suffix';
import { DocumentImageCropper } from '../DocumentImageCropper';
import { IUpdateDocumentImage } from '../DocumentReviewContainer';
import { FileDTO } from 'store/candidate/interfaces';

import styles from './styles.module.scss';
import { OriginalFileParams } from '../DocumentReviewContainer/types';
import { defaultOriginalFileParams } from '../DocumentReviewContainer/constants';
import { AnimatedLoaderModal } from '../../../../components/Loaders/AnimatedLoader';
import { GenericFormShort } from '../GenericDocumentForm/GenericFormShort';
import useFormik from '../../../../hooks/useFormik';
import { clientDateToServerDateString } from '../../../../functions/date/clientDateToServerDate';
import { IDocumentDetailField } from '../../../../common/document/interfaces/document-detail-field';
import { buildFieldVerified } from '../Utils/field-verified';
import { driverCdlDefaultVerification } from '../../../../common/document/ui/driver-document';
import { validationSchema } from './validationSchema';
import { DocumentTypeEnum } from '../../../../common/document/enums/document-type.enum';
import { listOfDocumentsCanChangeType } from '../../../../common/document/constants/list-of-documents-can-change-type';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';
import { ReactComponent as DocumentHiddenIcon } from '../../../../assets/images/document-hidden.svg';

type InputProps = {
  document: IDocument;
  onReject?: (reason: IRejectReasonDocument) => void;
  onApprove?: (data: { [key: string]: any }) => void;
  onUpdate?: (data: { [key: string]: any }) => void;
  onDownload?: () => void;
  onUpdateDocumentImage?: (data: IUpdateDocumentImage) => void;
  onPurge?: (document: IDocument) => void;
  titlePrev: string;
  titleNext: string;
  onPrev: () => void;
  onNext: () => void;
  isLoadingVerificationStatus?: boolean;
  activeTabDocumentFile: string;
  setActiveTabDocumentFile: (v: string) => void;
  setOriginalFileParams: (v: OriginalFileParams) => void;
  currentOriginalFile?: FileDTO;
  isLoadingOriginalFile: boolean;
  canChangeDocumentType?: boolean;
};

export const GenericDocumentView = ({
  isLoadingOriginalFile,
  onReject,
  onApprove,
  document,
  onDownload,
  onUpdateDocumentImage,
  onPurge,
  titlePrev,
  titleNext,
  onPrev,
  onNext,
  isLoadingVerificationStatus = false,
  activeTabDocumentFile,
  setOriginalFileParams,
  currentOriginalFile,
  canChangeDocumentType = false,
}: InputProps) => {
  const { isReadOnly } = useManager();
  const [polygon] = useState<any | null>();
  const [locked, setLocked] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentDocumentFile, setCurrentDocumentFile] = useState<
    DocumentFiles | undefined
  >();

  const handleCloseEditMode = () => {
    setIsEdit(false);
    setOriginalFileParams(defaultOriginalFileParams);
  };

  useEffect(() => {
    handleCloseEditMode();
  }, [document]);

  useEffect(() => {
    if (activeTabDocumentFile) {
      setCurrentDocumentFile(
        documentFileList.find(
          (document) =>
            document.documentName.toLowerCase() === activeTabDocumentFile
        )
      );
    }
  }, [activeTabDocumentFile]);

  useEffect(() => {
    setLocked(
      ![DocumentStatusEnum.UPLOADED, DocumentStatusEnum.NEEDS_REVIEW].includes(
        document.status
      )
    );
  }, [document]);

  const fieldVerified = useMemo(() => {
    const fieldVerified = buildFieldVerified(document?.details);
    return fieldVerified ?? driverCdlDefaultVerification;
  }, [document]);

  const documentFileList = useMemo(() => {
    const documentFiles = document.documentFiles.filter(
      (docFile) => !docFile.documentName.endsWith(originalSuffix)
    );

    if (document.documentType === DocumentTypeEnum.RETURN_TO_WORK) {
      return [documentFiles[0]];
    }

    return documentFiles.length >= 2 ? documentFiles : [documentFiles[0]];
  }, [document.documentFiles]);

  const initialValues = useMemo(() => {
    return (
      (document?.details?.reduce &&
        document.details.reduce(
          (
            hash: { [key: string]: any },
            currentValue: IDocumentDetailField
          ) => {
            if (
              currentValue?.fieldValue &&
              !Number.isNaN(Date.parse(currentValue.fieldValue))
            ) {
              const [date] = currentValue.fieldValue.split('T');
              const [y, m, d] = date.split('-');
              hash[currentValue.fieldName] = `${m}/${d}/${y}`;
            } else {
              hash[currentValue.fieldName] = currentValue?.fieldValue || '';
            }
            return hash;
          },
          {}
        )) ||
      {}
    );
  }, [document]);

  const documentValidationSchema = useMemo(() => {
    return document?.documentType !== DocumentTypeEnum.PHOTO_ID
      ? validationSchema
      : undefined;
  }, [document]);

  const { getFieldProps, getFieldErrorProp, handleSubmit } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: documentValidationSchema,
    onSubmit(value: { [key: string]: any }) {
      onApprove &&
        onApprove({
          ...value,
          issueDate: value.issueDate
            ? clientDateToServerDateString(new Date(value.issueDate))
            : undefined,
        });
    },
  });

  const handleApprove = () => {
    handleSubmit();
  };

  const handlePurge = () => {
    onPurge && onPurge(document);
  };

  const handleEdit = () => {
    if (currentDocumentFile?.documentName) {
      setOriginalFileParams({
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile?.documentName,
      });
      setIsEdit(true);
    }
  };

  const handleSaveImage = (file: Blob) => {
    handleCloseEditMode();
    if (
      onUpdateDocumentImage &&
      file &&
      document?.candidateDocumentId &&
      currentDocumentFile?.documentName
    ) {
      onUpdateDocumentImage({
        file: new File([file], currentDocumentFile?.documentName, {
          type: file.type,
        }),
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile.documentName,
      });
    }
  };

  if (document.status === DocumentStatusEnum.DELETED) {
    return (
      <div className={styles.root}>
        <Divider />
        <DocumentReviewHeader
          titlePrev={titlePrev}
          titleNext={titleNext}
          onPrev={onPrev}
          onNext={onNext}
          title={document.documentName}
          notifications={[]}
          canEditDocumentType={
            canChangeDocumentType &&
            listOfDocumentsCanChangeType.includes(document.documentType) &&
            [
              DocumentStatusEnum.NEEDS_REVIEW,
              DocumentStatusEnum.UPLOADED,
            ].includes(document.status)
          }
          document={document}
        />
        <div className={styles.content}>
          <div className={styles.purged}>The document has been purged</div>
        </div>
      </div>
    );
  }

  const canEditDocumentType = useMemo(() => {
    return (
      canChangeDocumentType &&
      !isReadOnly &&
      listOfDocumentsCanChangeType.includes(document.documentType) &&
      [DocumentStatusEnum.NEEDS_REVIEW, DocumentStatusEnum.UPLOADED].includes(
        document.status
      )
    );
  }, [document, canChangeDocumentType]);

  return (
    <div className={styles.root}>
      <Divider />
      <DocumentReviewHeader
        titlePrev={titlePrev}
        titleNext={titleNext}
        onPrev={onPrev}
        onNext={onNext}
        title={
          document.documentName +
          (document.status === DocumentStatusEnum.REJECTED ? ' - REJECTED' : '')
        }
        notifications={[]}
        canEditDocumentType={canEditDocumentType}
        document={document}
      />
      <div className={styles.content}>
        {isReadOnly ? (
          readOnlyViewableStatuses.includes(document.status) ? (
            <DocumentImageViewer
              polygon={polygon}
              currentDocument={currentDocumentFile}
              setCurrentDocument={setCurrentDocumentFile}
              documents={documentFileList}
            />
          ) : (
            <DocumentHiddenIcon />
          )
        ) : (
          <>
            {isEdit ? (
              !isLoadingOriginalFile && currentOriginalFile?.url ? (
                <DocumentImageCropper
                  fileUrl={currentOriginalFile?.url}
                  onSave={handleSaveImage}
                  onClose={handleCloseEditMode}
                  aspectRatio={1.235}
                />
              ) : (
                <AnimatedLoaderModal isLoading={isLoadingOriginalFile} />
              )
            ) : (
              <>
                <div className={styles.cropper}>
                  <DocumentImageViewer
                    polygon={polygon}
                    onDownload={
                      document.status === DocumentStatusEnum.APPROVED
                        ? onDownload
                        : undefined
                    }
                    onPurge={handlePurge}
                    onReject={!locked ? onReject : undefined}
                    onEdit={
                      currentDocumentFile?.fileUrl ? handleEdit : undefined
                    }
                    onApprove={!locked ? handleApprove : undefined}
                    currentDocument={currentDocumentFile}
                    setCurrentDocument={setCurrentDocumentFile}
                    documents={documentFileList}
                    isLoading={isLoadingVerificationStatus}
                  />
                </div>
                <div className={styles.formWrapper}>
                  {document.documentType !== DocumentTypeEnum.PHOTO_ID ? (
                    <GenericFormShort
                      getFieldProps={getFieldProps}
                      getFieldErrorProp={getFieldErrorProp}
                      fieldVerified={fieldVerified}
                      fieldPolygon={{}}
                      verificationResult={{}}
                      verificationStatus={{}}
                      onHoverField={() => undefined}
                      onUnHoverField={() => undefined}
                      disabled={locked}
                    />
                  ) : null}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <LogEvents logs={document.documentEvents} />
    </div>
  );
};
